import { z } from "zod";
import type { FocusProfile } from "./_focus-schema";
import type { FocusCopyPack } from "./quiz-focus-copy";
import type { StoryFunnel } from "./_story-funnel-schema";

/**
 * The 6 canonical Mars Men personas. Mirrors mars-men-lp-generator/static/audience_personas.md.
 * A quiz classifier MUST return one of these keys.
 */
export const PERSONA_KEYS = [
  "trt-alternative",
  "aging-men",
  "skeptical-buyer",
  "everyday-man",
  "performance",
  "glp1-user",
] as const;

export const PersonaKeySchema = z.enum(PERSONA_KEYS);
export type PersonaKey = z.infer<typeof PersonaKeySchema>;

/**
 * Render locale for widget chrome and deriver verdict copy. Defaults to "en".
 *
 * TS-only, deliberately NOT in QuizDefinitionSchema -- same rationale as
 * `emailGate`: it is a runtime-render flag that never touches `branching`,
 * `classifier`, or `deriveProfile`, so a localized clone stays
 * regression-identical to its source. Adding it to the Zod schema would force a
 * re-parse of every existing quiz for no validation benefit, since the type
 * system already constrains the value.
 */
export type Locale = "en" | "es";

/**
 * Which product's colours a quiz wears.
 *
 * This widget now sells two products. Every Boost surface is house mars-orange;
 * Ignite, the cognition stick pack, is yellow per its spec sheet -- and yellow
 * on every accent the quiz renders, not only the CTA. The value is stamped on
 * the web-component host by `web/src/main.tsx`, where four CSS variables in
 * `web/src/brand/tokens.css` re-point every accent utility at once -- including
 * the two that carry CONTRAST with the accent, which is why the swap is four
 * variables and not one.
 *
 * Omit it and the quiz renders house orange, which is what all 25 quizzes that
 * predate this field do -- byte-identically, since the default token values ARE
 * the old literals.
 *
 * ON `DiagnosticQuiz` ONLY, and that is the rule rather than an oversight.
 * Only the surfaces a diagnostic quiz renders were converted to the tokens.
 * Six screen types (`ruler`, `wheel`, `number`, `summary`, `signal-summary`,
 * `story`), the entire persona endgame (`BuildingProfile` -> `Result` ->
 * `Projection` -> `Offer` -> `ApplyingDiscount`) and every calculator surface
 * still hardcode `mars-orange`. A `PersonaQuiz` carrying this would run yellow
 * through its questions and snap back to orange at the verdict, with every gate
 * green -- `tests/smoke/ignite-accent.spec.ts` walks `quiz-focus`, which
 * reaches none of it. Widening this union member means converting that kind's
 * surfaces in the same change, and walking a quiz that reaches them.
 *
 * TS-only, deliberately NOT in `QuizDefinitionSchema` -- same rationale as
 * `locale` and `emailGate`: it is a runtime-render flag that never touches
 * `branching`, `classifier`, `deriveProfile` or `deriveFocusProfile`, so a
 * re-themed quiz stays regression-identical to its source. The type system
 * already constrains the value, so a Zod entry would force a re-parse of every
 * existing quiz for no validation benefit.
 */
export type QuizTheme = "ignite";

/**
 * Per-column numeric record produced by a `wheel` question (WP04), e.g.
 * `{ ft: 5, in: 6 }`. Keys are the wheel's column ids; values are always
 * plain numbers (no PII). This is a NEW union member alongside the
 * pre-existing string/string[]/number/null shapes -- additive, does not
 * change how any existing answer type parses.
 */
export const WheelAnswerSchema = z.record(z.string(), z.number());
export type WheelAnswer = z.infer<typeof WheelAnswerSchema>;

export const AnswerValueSchema = z.union([
  z.string(),
  z.array(z.string()),
  z.number(),
  WheelAnswerSchema,
  z.null(),
]);
export type AnswerValue = z.infer<typeof AnswerValueSchema>;

export const AnswersSchema = z.record(z.string(), AnswerValueSchema);
export type Answers = z.infer<typeof AnswersSchema>;

/**
 * Optional photo for a single/multi option (WP01). `alt` is required (not
 * optional/defaulted like InfoQuestionSchema.image.alt) because option images
 * are the PRIMARY selection cue in the 2-col grid layout -- an option with an
 * unlabeled image would be inaccessible in a way an info-screen decorative
 * image is not. `emoji` may still be authored alongside `image` for data
 * continuity (e.g. a future grid-off render); at render time image wins and
 * the emoji glyph is dropped (Question.tsx).
 */
const OptionImageSchema = z.object({
  src: z.string().min(1),
  alt: z.string().min(1),
});
export type OptionImage = z.infer<typeof OptionImageSchema>;

/**
 * Charset lock shared by every id that can end up in an `answer_id` /
 * analytics namespace (rule 22): option `value`s and, since WP04, `wheel`
 * column `id`s. Lowercase + digits + `_`/`-`, no leading underscore, no `+`
 * (the multi-select widget joins selected option ids with `+`).
 */
const ID_CHARSET_REGEX = /^[a-z0-9][a-z0-9_\-]*$/;
const ID_CHARSET_MESSAGE = "id must be lowercase, may contain digits/_/-, no leading _, no '+'";
const ISO_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-([0-2]\d|3[01])$/;
const ISODateStringSchema = z.string().regex(ISO_DATE_RE).refine((value) => {
  const [year = Number.NaN, month = Number.NaN, day = Number.NaN] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day
  );
}, "must be a valid ISO date");

const OptionSchema = z.object({
  value: z
    .string()
    .min(1)
    .max(64)
    .regex(ID_CHARSET_REGEX, "option " + ID_CHARSET_MESSAGE)
    .refine((v) => v !== "advance", "'advance' is reserved for info-screen telemetry"),
  label: z.string().min(1),
  emoji: z.string().optional(),
  pinned: z.boolean().optional(),
  image: OptionImageSchema.optional(),
});
export type QuestionOption = z.infer<typeof OptionSchema>;

const WhyWeAskSchema = z.object({
  heading: z.string().default("Why we ask"),
  body: z.string().min(1),
});
export type WhyWeAsk = z.infer<typeof WhyWeAskSchema>;

export const VariantTagSchema = z.enum(["control", "variant"]);
export type VariantTag = z.infer<typeof VariantTagSchema>;

const SingleQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("single"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  options: z.array(OptionSchema).min(2),
  randomizeOptions: z.boolean().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

const MultiQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("multi"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  options: z.array(OptionSchema).min(2),
  min: z.number().int().min(0).optional(),
  max: z.number().int().min(1).optional(),
  randomizeOptions: z.boolean().optional(),
  // WP02: gates the Continue button until every option in `options` is
  // selected -- a "confirmations" screen right before an offer (Phase-7
  // forced micro-commitment). Contradicts `exclusiveValues` (enforced in
  // QuizDefinitionSchema.superRefine): selecting an exclusive value makes
  // selecting every option simultaneously impossible.
  requireAll: z.boolean().optional(),
  // WP02: option ids that are mutually exclusive with the rest of the
  // screen's selection AND with each other (ShopFunnel "noneValues").
  // Selecting any exclusive value replaces the whole selection with just
  // that value; selecting any non-exclusive value drops every exclusive
  // value from the selection. Must be a subset of `options[].value`
  // (enforced in QuizDefinitionSchema.superRefine -- discriminatedUnion
  // members must stay raw ZodObjects, so cross-field checks live one level
  // up, same pattern as the number-question min/max checks below).
  exclusiveValues: z.array(z.string()).optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

const SliderQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("slider"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  min: z.number(),
  max: z.number(),
  step: z.number().optional(),
  minLabel: z.string().optional(),
  maxLabel: z.string().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

const TextQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("text"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  placeholder: z.string().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

const DateQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("date"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  minDate: ISODateStringSchema.optional(),
  maxDate: ISODateStringSchema.optional(),
  noDateLabel: z.string().min(1),
  noDateValue: z.string().regex(ID_CHARSET_REGEX),
  variantTag: VariantTagSchema.optional(),
});

/**
 * Numeric input question for calculator-style quizzes. Stores the answer as a
 * `number` in `answers[id]`, or `null` when the question is `optional` and the
 * user skipped it. Cross-field constraints (min < max, defaultValue in range)
 * are enforced by `QuizDefinitionSchema.superRefine` — `discriminatedUnion`
 * requires raw ZodObjects so the refinements live one level up.
 */
const NumberQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("number"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  unit: z.string().optional(),
  placeholder: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().positive().optional(),
  defaultValue: z.number().optional(),
  optional: z.boolean().optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

/**
 * Presentation-only unit toggle for a `ruler` question (WP04). The stored
 * answer is ALWAYS the ruler's own canonical `unit` -- toggling only changes
 * what is DISPLAYED. `factor` is canonical-per-alt-unit: canonical value =
 * altValue * factor. Example: `unit: "lbs"`, `altUnit: { label: "kg", factor:
 * 2.20462 }` because 1 kg = 2.20462 lbs, so displayedKg = canonicalLbs /
 * 2.20462 and canonicalLbs = displayedKg * 2.20462 (rounded to the nearest
 * `step`). Same convention for height: `unit: "in"`, `altUnit: { label:
 * "cm", factor: <cm-per-in> }`. This deviates from the original design
 * sketch's `toCanonical` function field on purpose (controller decision):
 * a numeric factor keeps the schema pure data (chat-editable, JSON-safe,
 * testable in isolation) and covers both known unit pairings. `factor` must
 * be > 0 so the conversion never divides by zero or flips sign.
 */
const RulerAltUnitSchema = z.object({
  label: z.string().min(1),
  factor: z.number().positive(),
});
export type RulerAltUnit = z.infer<typeof RulerAltUnitSchema>;

const AnalyticsAnswerModeSchema = z.enum(["value", "presence"]);
export type AnalyticsAnswerMode = z.infer<typeof AnalyticsAnswerModeSchema>;

/**
 * Tactile ruler input (WP04): a draggable/keyboard-adjustable slider that
 * stores a SINGLE canonical number in `answers[id]` -- the exact same answer
 * shape as `slider` (and `number`), so branching functions, analytics'
 * `answer_id` recording, and the walker all treat it identically without a
 * dedicated ruler branch anywhere except where the render/UI differs.
 * Mirrors ShopFunnel's `weightruler` mechanic (min 90 / max 400 / preset 184
 * lbs). Unlike `slider` (which seeds its handle from `min`), `defaultValue`
 * seeds the initial position -- pre-filled per the competitor reference,
 * zero-typing. Cross-field bounds (min < max, defaultValue in [min, max])
 * are enforced by QuizDefinitionSchema.superRefine, same pattern as
 * NumberQuestionSchema below, because discriminatedUnion members must stay
 * raw ZodObjects.
 */
const RulerQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("ruler"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  min: z.number(),
  max: z.number(),
  step: z.number().positive().optional(),
  defaultValue: z.number(),
  unit: z.string().min(1),
  valueFormat: z.enum(["number", "feet-inches"]).optional(),
  altUnit: RulerAltUnitSchema.optional(),
  // Opt-in privacy control for general quiz_events telemetry. "presence"
  // emits a stable non-sensitive sentinel while the canonical number remains
  // unchanged in Answers and the private quiz submission payload.
  analyticsAnswerMode: AnalyticsAnswerModeSchema.optional(),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

/**
 * One scroll-wheel column (WP04), e.g. feet or inches. `id` obeys the same
 * charset as option values (rule 22, `ID_CHARSET_REGEX`) because it becomes
 * both a key in the per-column answer record AND, in analytics, a segment of
 * the joined `answer_id` -- no `+`, no leading `_`, "advance" reserved, same
 * rationale as option ids.
 */
const WheelColumnSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(ID_CHARSET_REGEX, "column " + ID_CHARSET_MESSAGE)
    .refine((v) => v !== "advance", "'advance' is reserved for info-screen telemetry"),
  min: z.number(),
  max: z.number(),
  defaultValue: z.number(),
  suffix: z.string().optional(),
});
export type WheelColumn = z.infer<typeof WheelColumnSchema>;

/**
 * Tactile scroll-wheel input (WP04): 1-3 independently-adjustable numeric
 * columns, mirroring Primal Viking's `heightwheel` (ft 3-8, in 0-11, preset
 * 5'6"). Answer shape is a per-column record `Record<columnId, number>` --
 * deliberately NOT a combined canonical total. Wheel is generic: it does not
 * know it is rendering feet/inches or any other unit pairing, and it does
 * NOT bake in a ft/in-style combining rule. A quiz deriver that wants a
 * canonical total computes it itself from the record (e.g.
 * `answers.height.ft * 12 + answers.height.in`) -- that combining logic is
 * per-quiz authoring, not schema/runtime plumbing. A future column count
 * ceiling of 3 covers every observed competitor mechanic (ft/in, weeks/days,
 * hour/minute); raise it deliberately if a 4th column is ever needed, not by
 * accident. Cross-field bounds (unique column ids, each column's own
 * min < max, defaultValue in range) are enforced by
 * QuizDefinitionSchema.superRefine.
 */
const WheelQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("wheel"),
  prompt: z.string().min(1),
  helper: z.string().optional(),
  columns: z.array(WheelColumnSchema).min(1).max(3),
  whyWeAsk: WhyWeAskSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

/**
 * Stat-chart authority beat (WP05): a mid-quiz info screen extension carrying
 * one big clinical statistic + a citation, in the spirit of ShopFunnel's
 * `statchart` screen ("...up to 1 in 4 were never tested first. --
 * Baillargeon et al., Public Health Reports, 2015 (n=61,474)"). `bars` is an
 * OPTIONAL minimal visual accent -- a small inline SVG bar strip normalized
 * against its own max value, brand-orange scale, no axis/no labels. This is
 * deliberately not a charting library integration: one supporting statistic
 * per screen, not a dashboard. Renders inside the info body region, above
 * the CTA (rule 20 body -> CTA -> image order is unchanged).
 */
const InfoStatSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  cite: z.string().min(1).optional(),
  bars: z.array(z.number().min(0)).min(1).optional(),
  /**
   * 0-based index into `body`; the block renders directly after that paragraph.
   * Omit it and the stat renders after the last paragraph, which is where it
   * used to render unconditionally.
   *
   * Added 2026-08-17 for quiz-beer, whose stat supports a claim in the middle
   * of the argument -- pinned to the end it would have landed after the screen's
   * closing hand-off line, reading as an afterthought instead of as the
   * evidence for the sentence above it. Bounds-checked in
   * QuizDefinitionSchema.superRefine, same as `bullets.afterParagraph`.
   */
  afterParagraph: z.number().int().min(0).optional(),
});
export type InfoStat = z.infer<typeof InfoStatSchema>;

/**
 * Info screen: educational interstitial between questions. Renders rich content
 * with a Continue button. Stores `null` in answers so it doesn't influence the
 * classifier. Use to build conviction between answer pages.
 */
const InfoQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("info"),
  heading: z.string().min(1),
  /**
   * Optional pure function that rebuilds this screen's HEADING from the
   * answers given so far. Same contract as `deriveBody` immediately below --
   * same answers in, same string out, no side effects and no network -- so
   * the rendered screen stays reproducible from the persisted `answers` JSON.
   *
   * `heading` remains REQUIRED and is the fallback: it renders unchanged for
   * every screen that omits this field (all of them outside
   * `cortisol-weightloss/info-mechanism`), and it is what a reader of the
   * quiz file sees as the screen's canonical text. Author a `heading` that is
   * true for every user, then use `deriveHeading` only to make it more
   * specific -- never to say something the static fallback contradicts.
   *
   * Exists because `info-mechanism`'s static heading ("Your signs point to
   * one thing...") sat directly above `deriveBody`'s zero-signs paragraph
   * ("You reported none of the eight"), so a reader who denied every sign saw
   * a headline claiming his signs pointed somewhere. `deriveBody` already had
   * this seam for paragraphs; a heading needed the same one.
   */
  deriveHeading: z
    .custom<(answers: Answers) => string>((value) => typeof value === "function")
    .optional(),
  body: z.array(z.string().min(1)).min(1),
  /**
   * Optional pure function that rebuilds this screen's paragraphs from the
   * answers given so far. Same contract as `classifier` / `deriveProfile`
   * (rule 2): same answers in, same paragraphs out, no side effects and no
   * network -- so the rendered screen stays reproducible from the persisted
   * `answers` JSON.
   *
   * `body` remains REQUIRED and is the fallback: it renders unchanged for
   * every screen that omits this field (all of them outside
   * `quiz-weightloss-v2/info-reframe`), and it is what a reader of the quiz
   * file sees as the screen's canonical text. Author a `body` that is true for
   * every user, then use `deriveBody` only to make it more specific -- never
   * to say something the static fallback contradicts.
   *
   * Exists because the approved weight-loss v2 copy deck specifies a reframe
   * screen whose opener reads the user's own exercise and "what you've tried"
   * answers back to him, so the absolution beat stays true for a man who
   * rarely trains as well as for one who trains four times a week.
   *
   * Returns PARAGRAPHS OF SEGMENTS, not plain strings: the prototype wraps the
   * answer-derived clauses of this screen in `.answer-reference` so the reader
   * can see which words came from him, and a `readonly string[]` return type
   * threw that distinction away at the seam. `RecapSegment` is reused rather
   * than cloned -- it already means exactly "a run of text, flagged as
   * customer-provided or not", which is the same contract the plan recap needs;
   * only its name is recap-specific.
   *
   * `body` stays a plain `string[]`. A static fallback has no customer-provided
   * runs by definition, so making the fallback carry segments would only invite
   * an author to fake one.
   */
  deriveBody: z
    .custom<(answers: Answers) => readonly (readonly RecapSegment[])[]>(
      (value) => typeof value === "function",
    )
    .optional(),
  image: z
    .object({
      src: z.string().min(1),
      alt: z.string().default(""),
    })
    .optional(),
  animation: z.enum(["analyzing"]).optional(),
  autoAdvanceMs: z.number().int().min(0).optional(),
  cta: z.string().optional(),
  /**
   * Substrings of this screen's paragraphs to render in mars-orange, giving a
   * long editorial bridge a second reading path for skimmers: read only the
   * accented phrases and the argument still lands.
   *
   * Same authoring idiom as `meta.introAccent` (a substring of the headline,
   * highlighted where it occurs) rather than inline markup in `body`. That is
   * deliberate: `body` stays a plain `string[]` that reads as prose in the quiz
   * file, with no markup dialect to escape, and the accent list can be swapped
   * independently of the sentence.
   *
   * Applied AFTER `deriveBody`, at the segment level, so it composes with an
   * answer-derived body: a customer-provided run stays green (it is the user's
   * own words) and only authored runs can pick up an accent.
   *
   * Every entry must occur in at least one rendered paragraph -- enforced in
   * QuizDefinitionSchema.superRefine against `body`, since a typo'd accent is
   * otherwise a silent no-op. A screen using `deriveBody` is checked against
   * its static `body` fallback, which is the text a reader of the quiz file
   * sees, so keep accents that must survive derivation present in both.
   *
   * Optional and additive: omit it and paragraphs render exactly as before.
   */
  bodyAccents: z.array(z.string().min(1)).min(1).optional(),
  /**
   * A short parallel list rendered inside the body region with orange markers.
   *
   * Deliberately NOT a general "turn the body into bullets" switch. Bridges are
   * argument, not reference: bulleting narrative prose flattens cadence that is
   * doing persuasive work. Use this only for genuinely parallel items -- the
   * case it was built for is quiz-beer's three pathways ("make more T, unlock
   * the T that's handcuffed, stop the swap to estrogen"), which are three
   * things of the same kind and map 1:1 to the protocol page's PATHWAY cards.
   *
   * `afterParagraph` is a 0-based index into `body`; the list renders directly
   * after that paragraph so a closing line can still follow it. Omit it and the
   * list renders after the last paragraph. Bounds-checked in
   * QuizDefinitionSchema.superRefine.
   */
  bullets: z
    .object({
      items: z.array(z.string().min(1)).min(2),
      afterParagraph: z.number().int().min(0).optional(),
    })
    .optional(),
  stat: InfoStatSchema.optional(),
  variantTag: VariantTagSchema.optional(),
});

/**
 * WP09 mid-quiz diagnosis dashboard screen. A pass-through node in the
 * question flow (like `info`): answers nothing (advances with `null`), so it
 * never influences `branching`/`classifier` beyond participating in the walk.
 * This screen type carries only FLOW data -- the dashboard CONTENT comes from
 * `quiz.deriveSummary(answers)` (see `PersonaQuiz.deriveSummary` below),
 * mirroring the `deriveProfile` split (rule 11: pure function of
 * answers-so-far, reproducible server-side, Zod-parsed).
 *
 * `eyebrow` is optional author-facing copy (e.g. "Interim readout") rendered
 * above `heading`; omit it and `SummaryDashboard.tsx` falls back to a neutral
 * structural label. This is a deliberate one-field addition beyond the design
 * doc's literal "(id, prompt/heading, optional helper, cta label)" list --
 * see the WP09 implementation report for rationale.
 */
const SummaryQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("summary"),
  eyebrow: z.string().min(1).optional(),
  heading: z.string().min(1),
  helper: z.string().optional(),
  cta: z.string().optional(),
  variantTag: VariantTagSchema.optional(),
});

const SignalSummaryQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("signal-summary"),
  eyebrow: z.string().min(1).optional(),
  heading: z.string().min(1),
  helper: z.string().optional(),
  cta: z.string().min(1),
  testimonial: z
    .object({
      quote: z.string().min(1),
      author: z.string().min(1),
      badge: z.string().min(1).optional(),
      /**
       * Portrait for the quote. Same asset discipline as `meta.testimonial`
       * (rule 19): a purpose-built 256x256 WebP at ~13 KB, never the 10 MB
       * `testimonial-avatar.png`. WebP not AVIF -- libaom drops alpha on the
       * circular crop. Omit it and the row falls back to an initials disc, so
       * an unset avatar degrades rather than breaking the layout.
       */
      avatar: z.string().min(1).optional(),
    })
    .optional(),
  variantTag: VariantTagSchema.optional(),
});
/**
 * One answer-reactive copy bucket for an `editorial` screen. `heading` is
 * optional so a screen can vary only its body.
 */
const EditorialVariantSchema = z.object({
  heading: z.string().min(1).optional(),
  body: z.array(z.string().min(1)).min(1),
});
export type EditorialVariant = z.infer<typeof EditorialVariantSchema>;

const EditorialBylineSchema = z.object({
  name: z.string().min(1),
  credentials: z.string().min(1),
  avatar: z.string().min(1).optional(),
  /** Free-text reading-time / continuation label, e.g. "3-min read". */
  read: z.string().min(1).optional(),
});
export type EditorialByline = z.infer<typeof EditorialBylineSchema>;

const EditorialFootnoteSchema = z.object({
  marker: z.string().min(1).max(4),
  text: z.string().min(1),
});

/**
 * Editorial screen: an advertorial reading beat. A richer sibling of `info`
 * (byline, eyebrow, pull quote, footnotes) built for interactive-advertorial
 * quizzes, where the argument of a long-form advertorial runs the spine and
 * each "here's what happens to you" beat becomes a self-check.
 *
 * `info` was deliberately NOT widened: many live quizzes depend on it and it
 * is intentionally thin. Design:
 * `docs/specs/2026-07-10-cortisol-story-interactive-advertorial-design.md`.
 *
 * Like `info`/`summary` this is a pass-through node -- it advances with
 * `onAnswer(null)` and stores `null`, so it never influences the classifier.
 * Unlike them it renders past the fold by design (reading screen, not an
 * answer screen); see `.claude/widget-ui-internals.md`.
 *
 * ANSWER-REACTIVE BEATS. `reactive.token` names a numeric token from the
 * quiz's `deriveTokens(answers)` map. `variants` then selects the copy bucket
 * by that count: 0 -> `zero`, 1 -> `one`, >=2 -> `many`. Three buckets exist
 * because a single templated string breaks in two directions: an absolution
 * premise ("You're not imagining it") is a contradiction at zero, and
 * "{signCount} of the 8" is broken grammar at one. Cross-field rules live in
 * `QuizDefinitionSchema.superRefine` (discriminatedUnion members must stay
 * raw ZodObjects).
 */
const EditorialQuestionSchema = z.object({
  id: z.string().min(1),
  type: z.literal("editorial"),
  byline: EditorialBylineSchema.optional(),
  eyebrow: z.string().min(1).optional(),
  heading: z.string().min(1),
  body: z.array(z.string().min(1)).min(1),
  pullQuote: z.string().min(1).optional(),
  footnotes: z.array(EditorialFootnoteSchema).min(1).optional(),
  image: z
    .object({
      src: z.string().min(1),
      alt: z.string().default(""),
    })
    .optional(),
  reactive: z.object({ token: z.string().min(1) }).optional(),
  variants: z
    .object({
      zero: EditorialVariantSchema.optional(),
      one: EditorialVariantSchema.optional(),
      many: EditorialVariantSchema.optional(),
    })
    .optional(),
  animation: z.enum(["analyzing"]).optional(),
  autoAdvanceMs: z.number().int().min(0).optional(),
  cta: z.string().optional(),
  variantTag: VariantTagSchema.optional(),
});

/**
 * How long a story card holds when its author omits `ms`.
 *
 * Exported because `defineQuiz` validates with Zod but returns the AUTHORED
 * object, so this `.default()` never reaches the renderer -- a card written
 * without `ms` arrives at `StoryScreen` undefined. The component applies the
 * SAME constant as its runtime fallback, which is why it lives here rather
 * than being written twice.
 */
export const STORY_CARD_DEFAULT_MS = 5000;

/**
 * The longest either half of a story card's analytics id may be.
 *
 * `StoryScreen` composes `question_id` as `"<screenId>.<cardId>"`, and that
 * lands in `quiz_events.question_id`, which is `String(64)` (alembic 0002) and
 * validated by `^[a-z0-9_\-.]{1,64}$` in api/schemas/event.py. Capping each
 * half at 31 makes the longest possible pair 63 characters, so a composed id
 * can never be rejected by the validator (which 422s the WHOLE batch, taking
 * every other event buffered in the same 500ms window with it) nor truncated
 * by the column. Bound BOTH halves or the guarantee is only half a guarantee.
 */
const STORY_ID_MAX_LEN = 31;

/**
 * One card in a `story` screen's deck -- a timed, full-screen beat rendered by
 * `web/src/components/StoryScreen.tsx`. Every content field is optional
 * INDIVIDUALLY -- a card may be nothing but a photo (the operator's "sometimes
 * we will do a full image just like an Instagram Story") -- but a card with no
 * content AT ALL is rejected in `QuizDefinitionSchema.superRefine`: it would
 * parse cleanly and then hold a black screen for its whole duration.
 *
 * Named `StoryBeat` rather than `StoryCard` because `StoryCardSchema` below is
 * WP08's DERIVED deck, which is a different feature. Prose calls both cards.
 * See docs/specs/2026-09-04-story-cards-screen-design.md.
 */
export const StoryBeatSchema = z.object({
  id: z.string().min(1).max(STORY_ID_MAX_LEN).regex(ID_CHARSET_REGEX),
  photo: z
    .object({
      src: z.string().min(1),
      alt: z.string().default(""),
    })
    .optional(),
  eyebrow: z.string().min(1).optional(),
  head: z.string().min(1).optional(),
  body: z.string().min(1).optional(),
  /**
   * How long the card holds before advancing. Floors at 1.5s because anything
   * shorter cannot be read; caps at 15s because a card that outlasts patience
   * should be two cards.
   */
  ms: z.number().int().min(1500).max(15000).default(STORY_CARD_DEFAULT_MS),
  /**
   * The one button. Allowed on the LAST card only (enforced in
   * QuizDefinitionSchema.superRefine); that card runs no timer and waits.
   */
  cta: z.string().min(1).optional(),
});
export type StoryBeat = z.infer<typeof StoryBeatSchema>;

const StoryQuestionSchema = z.object({
  /**
   * Charset- and length-locked like the card ids, unlike every other screen
   * type's free-form `id`. This screen is the only one whose id is CONCATENATED
   * into an analytics key rather than sent as-is, so an id the events API would
   * reject (a capital, a space) takes down the whole event batch it rides in.
   */
  id: z.string().min(1).max(STORY_ID_MAX_LEN).regex(ID_CHARSET_REGEX, "story screen " + ID_CHARSET_MESSAGE),
  type: z.literal("story"),
  /** Two is a real authored deck; eight is where the segment bar stops being readable at 375px. */
  cards: z.array(StoryBeatSchema).min(2).max(8),
  variantTag: VariantTagSchema.optional(),
});

export const QuestionSchema = z.discriminatedUnion("type", [
  SingleQuestionSchema,
  MultiQuestionSchema,
  SliderQuestionSchema,
  TextQuestionSchema,
  DateQuestionSchema,
  NumberQuestionSchema,
  RulerQuestionSchema,
  WheelQuestionSchema,
  InfoQuestionSchema,
  SummaryQuestionSchema,
  SignalSummaryQuestionSchema,
  EditorialQuestionSchema,
  StoryQuestionSchema,
]);
export type Question = z.infer<typeof QuestionSchema>;

export const CaptureConfigSchema = z.object({
  fields: z.array(z.enum(["email", "phone"])).min(1),
  required: z.array(z.enum(["email", "phone"])).default([]),
  headline: z.string().optional(),
  subheadline: z.string().optional(),
  cta: z.string().optional(),
  disclaimer: z.string().optional(),
});
export type CaptureConfig = z.infer<typeof CaptureConfigSchema>;

/**
 * Quiz kind discriminator. `persona` quizzes route through `classifier` to one
 * of the 6 Mars Men personas and render a persona narrative. `calculator`
 * quizzes route through `compute` to a deterministic numeric result and render
 * a calculator-result surface (hero value, status pill, optional breakdown,
 * methodology). New kinds require a corresponding state-machine branch and
 * result component; do not add one without updating both.
 */
export const QUESTION_QUIZ_KINDS = ["persona", "calculator", "diagnostic"] as const;
export const QuestionQuizKindSchema = z.enum(QUESTION_QUIZ_KINDS);
export const QUIZ_KINDS = [...QUESTION_QUIZ_KINDS, "story-funnel"] as const;
export const QuizKindSchema = z.enum(QUIZ_KINDS);
export type QuizKind = z.infer<typeof QuizKindSchema>;

/**
 * CalculatorResult is the runtime output of a calculator quiz's `compute`
 * function. It is the calculator-mode analog of `TSuppressionProfile`: pure
 * function of `answers`, reproducible server-side from the persisted
 * submission. UI surfaces (hero, status, secondary values, breakdown) are
 * data-driven so a single component renders every calculator quiz.
 */
const CalculatorValueSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  value: z.number(),
  unit: z.string().min(1),
  altUnit: z
    .object({
      value: z.number(),
      unit: z.string().min(1),
    })
    .optional(),
  status: z.enum(["low", "normal", "high"]).optional(),
  referenceRange: z.string().optional(),
});
export type CalculatorValue = z.infer<typeof CalculatorValueSchema>;

const CalculatorBreakdownSegmentSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  pct: z.number().min(0).max(100),
});
export type CalculatorBreakdownSegment = z.infer<typeof CalculatorBreakdownSegmentSchema>;

const CalculatorBreakdownSchema = z.object({
  title: z.string().min(1),
  segments: z.array(CalculatorBreakdownSegmentSchema).min(2),
});
export type CalculatorBreakdown = z.infer<typeof CalculatorBreakdownSchema>;

const CalculatorStatusSchema = z.object({
  key: z.enum(["low", "normal", "high"]),
  label: z.string().min(1),
  message: z.string().min(1),
});
export type CalculatorStatus = z.infer<typeof CalculatorStatusSchema>;

/**
 * Reference table row -- mirrors the "Reference Ranges" table on the original
 * standalone calculator (metric / your value / normal range / status).
 */
const CalculatorReferenceRowSchema = z.object({
  metric: z.string().min(1),
  value: z.string().min(1),
  range: z.string().min(1),
  status: z.enum(["low", "normal", "high"]),
});
export type CalculatorReferenceRow = z.infer<typeof CalculatorReferenceRowSchema>;

/**
 * One row on the "What This Means" page -- a linear scale visualization that
 * positions a value along a Low / Normal / High band with labeled tick marks.
 */
const CalculatorScaleSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  value: z.number(),
  unit: z.string().min(1),
  status: z.enum(["low", "normal", "high"]),
  min: z.number(),
  max: z.number(),
  lowMax: z.number(),
  highMin: z.number(),
  lowCopy: z.string().optional(),
  normalCopy: z.string().optional(),
  highCopy: z.string().optional(),
});
export type CalculatorScale = z.infer<typeof CalculatorScaleSchema>;

/**
 * Interpretation card on the "What This Means" page. The `accent` controls the
 * left-border color and matches a status/semantic vocabulary so the page reads
 * as a quick at-a-glance summary.
 */
const CalculatorInterpretationCardSchema = z.object({
  key: z.string().min(1),
  emoji: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
  accent: z.enum(["green", "orange", "red", "blue"]),
});
export type CalculatorInterpretationCard = z.infer<typeof CalculatorInterpretationCardSchema>;

const CalculatorNextStepSchema = z.object({
  emoji: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
});
export type CalculatorNextStep = z.infer<typeof CalculatorNextStepSchema>;

const CalculatorInterpretationSchema = z.object({
  scales: z.array(CalculatorScaleSchema).optional(),
  cards: z.array(CalculatorInterpretationCardSchema).min(1),
  nextSteps: z
    .object({
      title: z.string().min(1),
      items: z.array(CalculatorNextStepSchema).min(1),
    })
    .optional(),
});
export type CalculatorInterpretation = z.infer<typeof CalculatorInterpretationSchema>;

const CalculatorRecommendationLevelSchema = z.object({
  eyebrow: z.string().min(1),
  title: z.string().min(1),
  recommended: z.boolean().optional(),
});
export type CalculatorRecommendationLevel = z.infer<typeof CalculatorRecommendationLevelSchema>;

const CalculatorRecommendationSchema = z.object({
  headline: z.string().min(1),
  headlineAccent: z.string().optional(),
  body: z.string().min(1),
  levels: z.array(CalculatorRecommendationLevelSchema).min(1),
  cta: z.object({
    label: z.string().min(1),
    href: z.string().min(1),
  }),
});
export type CalculatorRecommendation = z.infer<typeof CalculatorRecommendationSchema>;

const CalculatorBannerSchema = z.object({
  emoji: z.string().min(1),
  title: z.string().min(1),
  body: z.string().min(1),
});
export type CalculatorBanner = z.infer<typeof CalculatorBannerSchema>;

export const CalculatorResultSchema = z.object({
  hero: CalculatorValueSchema,
  status: CalculatorStatusSchema,
  banner: CalculatorBannerSchema.optional(),
  secondaryValues: z.array(CalculatorValueSchema).optional(),
  breakdown: CalculatorBreakdownSchema.optional(),
  referenceTable: z.array(CalculatorReferenceRowSchema).optional(),
  interpretation: CalculatorInterpretationSchema.optional(),
  recommendation: CalculatorRecommendationSchema.optional(),
  methodology: z.string().optional(),
  disclaimer: z.string().optional(),
});
export type CalculatorResult = z.infer<typeof CalculatorResultSchema>;

export const ExperimentSchema = z.object({
  intelligemsTestId: z.string().min(8).max(64),
  // Friendly name surfaced in the admin status bar so operators see what's
  // running without translating UUIDs. Format convention: "MM-0067 Change
  // First Page" — Intelligems ticket id + short description.
  name: z.string().min(1).max(120),
});
export type Experiment = z.infer<typeof ExperimentSchema>;

/**
 * Quiz definition. Branching and classifier are pure functions of `answers`.
 *
 * - branching(answers, currentQuestionId): the id of the next question, or null
 *   if the questions phase is complete (advances to the email gate).
 * - classifier(answers): the persona key to assign to this submission.
 */
export const QuizTestimonialSchema = z.object({
  quote: z.string().min(1),
  author: z.string().min(1),
  avatar: z.string().optional(),
  variantTag: VariantTagSchema.optional(),
});
export type QuizTestimonial = z.infer<typeof QuizTestimonialSchema>;

const ProofMarqueeImageSchema = z.object({
  src: z.string().min(1),
  alt: z.string().min(1),
});

/**
 * Before/after proof marquee (WP05): an auto-scrolling photo strip rendered
 * below the FIRST question's options -- social proof at the highest
 * drop-off point in any quiz (Q1 drop-off benchmark is 50-60%). Pure CSS
 * translate loop (keyframes live in web/src/brand/tokens.css, the shadow
 * stylesheet -- rule 5/17 mean this must never move to document.head).
 * `images.min(2)` because a single-photo "loop" duplicated against itself
 * doesn't read as a marquee. `speedSec` defaults to 30 in the component
 * (NOT via a Zod `.default`, since `defineQuiz`/`defineCalculatorQuiz`
 * discard the parsed object and return the authored one -- see their
 * doc comments) mirroring the competitor reference ~30s cycle.
 */
const ProofMarqueeSchema = z.object({
  images: z.array(ProofMarqueeImageSchema).min(2),
  speedSec: z.number().positive().optional(),
  /**
   * Where the strip renders relative to the first question.
   *
   * `below-options` (the default, and the original and only behaviour) puts it
   * after the options, where it can never push the answer pills down. That
   * guarantee is real and measured -- pill geometry is byte-identical with and
   * without the marquee. But so is its cost: measured on the real widget the
   * strip lands at 798 on a 667-tall viewport and 876 on a 768-tall one, so it
   * is below the fold on both, on the one screen users leave WITHOUT
   * scrolling. Proof that the abandoning population never renders is not
   * proof.
   *
   * `above-prompt` renders it before the question prompt instead, which does
   * cost fold budget and therefore needs the author to make room -- on quiz-v6
   * there is ~300px of intro between the headline and the prompt, so swapping
   * the testimonial card for the strip is roughly free.
   *
   * Deliberately an enum of two rather than a boolean or a free offset: those
   * are the two positions the surrounding layout actually has, and a number
   * would invite fine-tuning a fold budget that only a real render can settle.
   */
  placement: z.enum(["below-options", "above-prompt"]).optional(),
  /**
   * Restricts the marquee to one Intelligems arm, exactly like
   * `meta.testimonial.variantTag`. Honoured by `visibleMeta()` in
   * web/src/state/visibleQuestions.ts.
   *
   * Needed because the marquee's only sanctioned home is question one, where
   * an A/B is mandatory (MM-0093 showed this quiz punishes unvalidated
   * screen-one changes), and screen-level tagging cannot reach a field on
   * `meta`. Untagged means "show on every arm", so existing quizzes are
   * unaffected.
   */
  variantTag: VariantTagSchema.optional(),
});
export type ProofMarquee = z.infer<typeof ProofMarqueeSchema>;

export const QuizMetaSchema = z.object({
  title: z.string().min(1),
  introHeadline: z.string().min(1),
  // Optional accent word rendered in mars-orange inside the (CSS-uppercased)
  // intro H1. QuizIntro defaults to "really" when omitted, so existing quizzes
  // (quiz-v6/quiz-v7/cortisol) are byte-identical. quiz-trt-switch sets "stuck".
  introAccent: z.string().min(1).max(40).optional(),
  introBody: z.string().optional(),
  // Optional bold, non-italic directive rendered directly below introBody on the
  // single-page calculator intro card (e.g. "Enter your labs to estimate yours.").
  // Creates a soft-context (italic body) -> hard-instruction (bold) hierarchy.
  // Only the single-page CalcIntro reads it; omit it and the card renders
  // introBody alone, so existing quizzes are byte-identical.
  introBodyEmphasis: z.string().min(1).max(120).optional(),
  // Optional row of short uppercase Martian Mono chips rendered ABOVE the intro
  // H1 on screen 1, in authored order (first chip orange, the rest muted --
  // the approved quiz-weightloss-v2 prototype's treatment). Omit the field and
  // QuizIntro renders no row at all, so the other quizzes are byte-identical.
  //
  // The caps are a fold contract, not style policing. At 375px the intro column
  // is 343px; a chip costs 22px of chrome (padding + border) plus ~7.8px per
  // uppercase character at text-[10px]/tracking-[0.08em], and the row is
  // flex-wrap. Two 18-char chips plus the 8px gap need ~333px -- inside 343 --
  // so `max(2)` + `max(18)` together make a WRAPPED row unrepresentable. That
  // matters because a wrap turns a ~26px row into a ~65px one: the prototype's
  // own labels ("2-Minute Assessment" + "Personalized 90-Day Plan") need ~358px
  // and were reverted for blowing the fold. Both caps are load-bearing; raising
  // either one requires re-measuring screen 1 at 375x667 with no preview banner.
  // Max 28 here is the standalone-intro ceiling; quizzes WITHOUT `introCta`
  // are held to 18 by QuizDefinitionSchema.superRefine, because there the row
  // shares screen 1 with question 1 and a wrapped badge moves the rule 19
  // fold without moving the model that guards it.
  introBadges: z.array(z.string().min(1).max(28)).min(1).max(2).optional(),
  /**
   * Promotes the intro block to a SCREEN OF ITS OWN, with this string as its
   * forward button, instead of stacking it above the first question.
   *
   * Opt-in and inert by default: a quiz that omits it renders exactly as
   * before -- `QuizIntro` above question 1, progress bar from question 2 on
   * (rule 19). All quizzes but `quiz-weightloss-v2` omit it.
   *
   * Why it exists: the approved weight-loss v2 copy deck (and the prototype
   * it was signed off from) treat the intro as its own step -- headline, full
   * lede, "Start My Assessment", testimonial -- with "QUESTION 1 OF 14" on the
   * screen after. Stacked onto question 1 that copy puts the first answer pill
   * 111px BELOW the iPhone SE fold; on its own screen the question's pills sit
   * at 300/368 against a 628 fold. So this is what makes the authored intro
   * copy shippable at all, not a styling preference.
   *
   * It adds no question: `questions`, `branching`, `classifier` and the
   * analytics question-type allowlist are untouched. Intro-to-question
   * drop-off stays measurable because `started` fires on mount, before this
   * screen is dismissed, so it is `started` minus the first `question_viewed`.
   */
  introCta: z.string().min(1).max(40).optional(),
  testimonial: QuizTestimonialSchema.optional(),
  proofMarquee: ProofMarqueeSchema.optional(),
  /**
   * Restricts the ENTIRE intro block -- badges, H1, lede and testimonial --
   * to one Intelligems arm. Same mechanism as `meta.testimonial.variantTag`
   * and `meta.proofMarquee.variantTag`, one level up, and honoured by
   * `introVisible()` in web/src/state/visibleQuestions.ts.
   *
   * It is ONE field rather than three coordinated field-level tags because
   * the thing being tested is all-or-nothing: three tags would make
   * "headline hidden, lede showing" representable and nothing wants that.
   * It also could not express "and no progress bar either", which is a
   * decision about the SCREEN, not about a field -- see `showIntro` in
   * web/src/MarsMenQuiz.tsx.
   *
   * Untagged means "render on every arm", so every quiz that omits it is
   * byte-identical. `effectiveVariant(null)` is "control", so a visitor for
   * whom Intelligems never loads sees the intro: exposure loss renders as
   * control, which is the existing contract.
   *
   * First use: MM-0102, authored as "control" on the 22 Boost slugs.
   */
  introVariantTag: VariantTagSchema.optional(),
  // WP03 name capture + {name} interpolation. Points at the `text` question
  // whose answer is the user's preferred name (must exist and be type "text"
  // -- enforced in QuizDefinitionSchema.superRefine). When set, quiz-authored
  // strings on the question flow (prompt, helper, info heading, info body)
  // may carry a `{name}` token that `web/src/lib/interpolate.ts` replaces at
  // render time with the trimmed, HTML-stripped, 24-char-capped, title-cased
  // first token of the answer. Quizzes stay pure data (rule 2): tokens in
  // strings, never function-valued fields. Absent field = no interpolation,
  // literal strings render untouched (byte-identical live quizzes).
  nameQuestionId: z.string().min(1).optional(),
  // Rendered VERBATIM in place of `{name}` when the answer is missing/blank
  // (e.g. the capture screen sits on an unvisited branch). When this too is
  // absent the token collapses cleanly: "Almost there, {name}!" degrades to
  // "Almost there!" (see interpolate.ts cleanup). Max 24 mirrors the render
  // cap on captured names.
  nameFallback: z.string().min(1).max(24).optional(),
  // Interactive-advertorial quizzes lead with an `editorial` hero beat, which
  // IS their intro. Rule 19 would otherwise stack the QuizIntro hero on top of
  // it (it fires whenever answeredCount === 0). An EXPLICIT flag, deliberately
  // not inferred from "no meta.testimonial + a leading editorial screen":
  // omitting the testimonial is an endorsed pattern when no congruent review
  // exists, so an inferred signal would silently strip a future quiz's intro.
  editorialLed: z.boolean().optional(),
});
export type QuizMeta = z.infer<typeof QuizMetaSchema>;

/**
 * One screen relocation in the `variant` arm, expressed against an ANCHOR
 * screen rather than a numeric position.
 *
 * Anchors, not indices, on purpose. "Move it to position 5" is not a stable
 * instruction across quizzes: counting every screen and counting only the
 * answerable ones diverge as soon as an interstitial appears, so position 7 on
 * cortisol is `body-composition` under one reading and `sugar-cravings` under
 * the other. An anchor says exactly one thing, reads as the intent
 * ("age goes after when-started"), and survives someone inserting a screen
 * later. Positional intent is translated to an anchor at authoring time.
 *
 * Exactly one of `after` / `before`; `before` also expresses "make this first"
 * by anchoring on the current first screen.
 */
export const VariantMoveSchema = z
  .object({
    id: z.string().min(1),
    after: z.string().min(1).optional(),
    before: z.string().min(1).optional(),
  })
  .refine((m) => (m.after === undefined) !== (m.before === undefined), {
    message: "a variantMove needs exactly one of `after` or `before`",
  });
export type VariantMove = z.infer<typeof VariantMoveSchema>;

/**
 * Apply `moves` to a control screen order, left to right, returning the
 * variant order. Pure; the input array is not mutated.
 *
 * Each move is resolved against the order AS IT STANDS at that point, so a
 * later move sees the effect of an earlier one. That makes a list of moves read
 * like a sequence of edits, which is how an operator describes them.
 *
 * A move is always a relocation, never an insertion or a removal, so the output
 * is guaranteed to be a permutation of the input. That is what lets the variant
 * arm share the control arm's `answers` keys, and therefore its classifier,
 * `deriveProfile`, and exhaustive regression locks, untouched.
 */
export function applyVariantMoves(order: string[], moves: VariantMove[]): string[] {
  let out = [...order];
  for (const move of moves) {
    const from = out.indexOf(move.id);
    if (from < 0) continue; // validated at parse time; defensive here
    out = [...out.slice(0, from), ...out.slice(from + 1)];
    const anchor = move.after ?? move.before!;
    const at = out.indexOf(anchor);
    if (at < 0) {
      out.splice(from, 0, move.id); // anchor vanished: leave it put
      continue;
    }
    out.splice(move.after !== undefined ? at + 1 : at, 0, move.id);
  }
  return out;
}

export const QuizDefinitionSchema = z
  .object({
    id: z.string().min(1),
    slug: z.string().min(1).max(64).regex(/^[a-z0-9-]+$/),
    kind: QuestionQuizKindSchema.optional(),
    meta: QuizMetaSchema,
    questions: z.array(QuestionSchema).min(1),
    experiment: ExperimentSchema.optional(),
    // Screen reordering for the `variant` arm. Additive: omit it and the
    // variant arm walks the control order, which is what every quiz did before
    // this existed. Requires `experiment` to have any effect at all, since
    // `effectiveVariant(null)` is "control".
    variantMoves: z.array(VariantMoveSchema).min(1).optional(),
    capture: CaptureConfigSchema.optional(),
  })
  .superRefine((quiz, ctx) => {
    // -- variantMoves ------------------------------------------------------
    // Every failure here is one a typo would otherwise turn into a SILENT
    // no-op: an unknown id or anchor would leave the order untouched and the
    // experiment would quietly serve control to both arms, which looks exactly
    // like a null result. Fail the build instead.
    if (quiz.variantMoves) {
      const ids = new Set(quiz.questions.map((q) => q.id));
      const moved = new Set<string>();
      quiz.variantMoves.forEach((move, i) => {
        const at = (msg: string, path: (string | number)[]) =>
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: msg, path });

        if (!ids.has(move.id)) {
          at(`variantMoves[${i}].id "${move.id}" is not a screen on this quiz.`,
            ["variantMoves", i, "id"]);
        }
        if (moved.has(move.id)) {
          at(`variantMoves moves "${move.id}" more than once; collapse it into a single move.`,
            ["variantMoves", i, "id"]);
        }
        moved.add(move.id);

        const anchor = move.after ?? move.before;
        const key = move.after !== undefined ? "after" : "before";
        if (anchor !== undefined && !ids.has(anchor)) {
          at(`variantMoves[${i}].${key} "${anchor}" is not a screen on this quiz.`,
            ["variantMoves", i, key]);
        }
        if (anchor === move.id) {
          at(`variantMoves[${i}] anchors "${move.id}" to itself.`,
            ["variantMoves", i, key]);
        }
      });

      // A move list that changes nothing is almost certainly not what was
      // intended, and it would ship an experiment whose arms are identical.
      const control = quiz.questions.map((q) => q.id);
      const variant = applyVariantMoves(control, quiz.variantMoves);
      if (variant.every((id, i) => id === control[i])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "variantMoves produces an order identical to control; the variant arm would be indistinguishable.",
          path: ["variantMoves"],
        });
      }
      // `variantMoves` without `experiment` can never run: the resolver is only
      // called when `experiment` is set, and effectiveVariant(null) is
      // "control". Allowed on purpose so the mechanism can ship inert ahead of
      // the Intelligems test being created, but it must be deliberate, so it is
      // surfaced by npm run validate-quiz rather than being a parse error.
    }

    // Intro badge length is capped by LAYOUT, not by taste, so the cap depends
    // on which layout the quiz uses.
    //
    // Stacked (no `introCta`): the badge row shares screen 1 with question 1,
    // and the rule 19 fold model bills it as a constant 26px on the stated
    // assumption that two labels of <=18 chars cannot wrap at 375px. A longer
    // label wraps, the row silently becomes 52px, and the model keeps passing
    // while the real fold moves. Hence the hard 18 here.
    //
    // Standalone (`introCta` set): the row has a screen to itself, nothing
    // below it competes for the fold, and the schema-level max of 28 is the
    // only limit -- which is what lets quiz-weightloss-v2 carry the approved
    // deck's full "2-Minute Assessment".
    if (quiz.meta.introCta === undefined && quiz.meta.introBadges) {
      quiz.meta.introBadges.forEach((badge, index) => {
        if (badge.length > 18) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              `introBadges[${index}] is ${badge.length} chars; the stacked intro layout caps them at 18 ` +
              `so the row cannot wrap and move the rule 19 fold. Set meta.introCta to give the intro ` +
              `its own screen, or shorten the label.`,
            path: ["meta", "introBadges", index],
          });
        }
      });
    }

    // -- meta.introVariantTag --------------------------------------------------
    // Both combinations below are SILENT no-ops, which is the failure mode
    // rule 25b's variantMoves checks exist for: an inert tag ships an
    // experiment whose two arms are identical and reads back as a null
    // result. Fail the build instead.
    if (quiz.meta.introVariantTag !== undefined) {
      if (quiz.meta.introCta !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "meta.introVariantTag cannot be combined with meta.introCta: that intro is a SCREEN " +
            "of its own, so hiding it removes a whole step rather than a block. Tag the screen " +
            "with variantTag, or run that quiz as its own experiment.",
          path: ["meta", "introVariantTag"],
        });
      }
      if (quiz.meta.editorialLed === true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "meta.introVariantTag has nothing to hide on an editorialLed quiz: QuizIntro is " +
            "already suppressed there (rule 19 opt-out), so both arms would render the same " +
            "screen while the dashboard reported two.",
          path: ["meta", "introVariantTag"],
        });
      }
    }

    // WP03: meta.nameQuestionId must reference an existing `text` question.
    // Cross-field between meta and questions, so it lives here (rule 3 loud
    // boot failure), not on QuizMetaSchema.
    if (quiz.meta.nameQuestionId !== undefined) {
      const target = quiz.questions.find((q) => q.id === quiz.meta.nameQuestionId);
      if (!target) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `nameQuestionId "${quiz.meta.nameQuestionId}" does not match any question id`,
          path: ["meta", "nameQuestionId"],
        });
      } else if (target.type !== "text") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `nameQuestionId "${quiz.meta.nameQuestionId}" must reference a text question (got "${target.type}")`,
          path: ["meta", "nameQuestionId"],
        });
      }
    }
    quiz.questions.forEach((q, qIdx) => {
      if ((q.type === "single" || q.type === "multi") && q.randomizeOptions) {
        const movable = q.options.filter((o) => !o.pinned).length;
        if (movable < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "randomizeOptions needs at least 2 non-pinned options to reorder",
            path: ["questions", qIdx, "randomizeOptions"],
          });
        }
      }
      if (q.type === "multi") {
        const optionValues = new Set(q.options.map((o) => o.value));
        const exclusiveValues = q.exclusiveValues ?? [];
        exclusiveValues.forEach((v, i) => {
          if (!optionValues.has(v)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `exclusiveValues[${i}] "${v}" is not one of this question's option values`,
              path: ["questions", qIdx, "exclusiveValues", i],
            });
          }
        });
        if (exclusiveValues.length > 0 && q.requireAll === true && q.options.length > 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "requireAll cannot be combined with a non-empty exclusiveValues -- selecting an exclusive option makes selecting every option simultaneously impossible",
            path: ["questions", qIdx, "requireAll"],
          });
        }
      }
      if (q.type === "number") {
        if (q.min !== undefined && q.max !== undefined && q.min >= q.max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "min must be strictly less than max",
            path: ["questions", qIdx, "min"],
          });
        }
        if (q.defaultValue !== undefined) {
          if (q.min !== undefined && q.defaultValue < q.min) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "defaultValue must be >= min",
              path: ["questions", qIdx, "defaultValue"],
            });
          }
          if (q.max !== undefined && q.defaultValue > q.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "defaultValue must be <= max",
              path: ["questions", qIdx, "defaultValue"],
            });
          }
        }
      }

      // `stickyCta` was REMOVED 2026-09-11. It was an opt-in flag asking an
      // author to decide, per screen, whether the CTA fell below the fold --
      // which `position: sticky` already answers by itself, correctly, for
      // whatever the content happens to be that day. Every info CTA is pinned
      // now (see `InfoScreen` in web/src/components/Question.tsx); there is no
      // flag to validate, and no `stickyCta`/`image` pair to reject.
      //
      // Rule 20's ORDER changes as a consequence: an info screen with an image
      // renders `body -> image` with the button pinned over it. That is
      // consistent with the rule's reason -- it exists to keep the CTA above
      // the fold when the image is tall.

      // A `bodyAccents` entry that matches nothing is a silent no-op: the screen
      // renders, the phrase just never turns orange, and nothing fails. Checked
      // against the static `body` even when `deriveBody` is set, because `body`
      // is the fallback every reader of the quiz file sees.
      if (q.type === "info" && q.bodyAccents) {
        const haystack = q.body.join("\n");
        q.bodyAccents.forEach((accent, aIdx) => {
          if (!haystack.includes(accent)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `bodyAccents[${aIdx}] ${JSON.stringify(accent)} does not occur in any body paragraph, so it would never render highlighted. Fix the substring (it must match exactly, including punctuation) or drop it.`,
              path: ["questions", qIdx, "bodyAccents", aIdx],
            });
          }
        });
      }

      // An out-of-range `afterParagraph` would silently fall back to "render at
      // the end", quietly moving a list the author placed mid-argument.
      if (q.type === "info") {
        for (const [field, idx] of [
          ["bullets", q.bullets?.afterParagraph],
          ["stat", q.stat?.afterParagraph],
        ] as const) {
          if (idx !== undefined && idx >= q.body.length) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${field}.afterParagraph is ${idx} but this screen has ${q.body.length} paragraph(s) (valid indices 0..${q.body.length - 1}).`,
              path: ["questions", qIdx, field, "afterParagraph"],
            });
          }
        }
      }

      if (q.type === "date" && q.minDate !== undefined && q.maxDate !== undefined && q.minDate > q.maxDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "minDate must be less than or equal to maxDate",
          path: ["questions", qIdx, "minDate"],
        });
      }
      // Editorial reactive-variant rules. Cross-field, so they live here
      // rather than on EditorialQuestionSchema (raw ZodObject constraint).
      if (q.type === "editorial" && q.variants !== undefined) {
        if (q.reactive === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "variants requires `reactive` -- there is no count to bucket on without a reactive token",
            path: ["questions", qIdx, "reactive"],
          });
        }
        if (q.variants.many === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "variants.many is required -- it is the fall-through bucket for every count >= 2",
            path: ["questions", qIdx, "variants", "many"],
          });
        }
        // Only `many` may interpolate a token. `zero`/`one` state their count
        // in words, so a rendered digit can never land beside a mismatched
        // noun ("1 signs", "0 of the classic signs").
        (["zero", "one"] as const).forEach((bucket) => {
          const v = q.variants?.[bucket];
          if (!v) return;
          const strings = [...(v.heading === undefined ? [] : [v.heading]), ...v.body];
          if (strings.some((s) => /\{[a-zA-Z][a-zA-Z0-9_]*\}/.test(s))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `variants.${bucket} must not contain a {token} -- state the count in words so a digit never renders beside a mismatched noun`,
              path: ["questions", qIdx, "variants", bucket],
            });
          }
        });
      }

      // WP04: ruler cross-field bounds (min < max, defaultValue in range).
      // Same shape as the number-question checks above -- `min`/`max`/
      // `defaultValue` are required (not optional) on RulerQuestionSchema, so
      // there is no "unbounded" branch to consider here.
      if (q.type === "ruler") {
        if (q.valueFormat === "feet-inches" && q.unit !== "in") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "feet-inches valueFormat requires unit to be in",
            path: ["questions", qIdx, "valueFormat"],
          });
        }
        if (q.min >= q.max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "min must be strictly less than max",
            path: ["questions", qIdx, "min"],
          });
        }
        if (q.defaultValue < q.min || q.defaultValue > q.max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "defaultValue must be within [min, max]",
            path: ["questions", qIdx, "defaultValue"],
          });
        }
      }

      // WP04: wheel cross-field bounds -- unique column ids (they become
      // both answer-record keys and analytics answer_id segments, so a
      // duplicate would silently collide) and, per column, min < max with
      // defaultValue in range.
      if (q.type === "wheel") {
        const seenColumnIds = new Set<string>();
        q.columns.forEach((col, cIdx) => {
          if (seenColumnIds.has(col.id)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `duplicate column id "${col.id}"`,
              path: ["questions", qIdx, "columns", cIdx, "id"],
            });
          }
          seenColumnIds.add(col.id);
          if (col.min >= col.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "column min must be strictly less than max",
              path: ["questions", qIdx, "columns", cIdx, "min"],
            });
          }
          if (col.defaultValue < col.min || col.defaultValue > col.max) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "column defaultValue must be within [min, max]",
              path: ["questions", qIdx, "columns", cIdx, "defaultValue"],
            });
          }
        });
      }

      // Story-screen cross-field rules (WP-story-cards): unique card ids, cta
      // restricted to the last card, a card that carries no content at all,
      // and a photo-only card must describe itself since the photo IS the
      // content rather than decoration behind words.
      if (q.type === "story") {
        const seen = new Set<string>();
        q.cards.forEach((c, ci) => {
          if (seen.has(c.id)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["questions", qIdx, "cards", ci, "id"],
              message: `story screen "${q.id}" has a duplicate card id "${c.id}"`,
            });
          }
          seen.add(c.id);

          if (c.cta !== undefined && ci !== q.cards.length - 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["questions", qIdx, "cards", ci, "cta"],
              message: `story card "${c.id}": a cta is allowed on the last card only`,
            });
          }

          // Every content field is individually optional, so a card carrying
          // NONE of them parses cleanly and then holds a black screen for its
          // whole duration -- a louder failure than the undescribed photo
          // below, and one nothing else catches.
          if (
            c.photo === undefined &&
            c.eyebrow === undefined &&
            c.head === undefined &&
            c.body === undefined
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["questions", qIdx, "cards", ci],
              message: `story card "${c.id}" has no content: it needs at least one of photo, eyebrow, head or body`,
            });
          }

          // A photo-only card IS the content, so it cannot be decorative. A
          // photo sitting behind words keeps alt: "".
          const wordless = c.head === undefined && c.body === undefined;
          if (c.photo !== undefined && wordless && c.photo.alt.trim() === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["questions", qIdx, "cards", ci, "photo", "alt"],
              message: `story card "${c.id}" is a photo with no words, so photo.alt must describe it`,
            });
          }
        });
      }
    });
  });

export type QuizDefinition = z.infer<typeof QuizDefinitionSchema>;

/**
 * T-suppression profile dimensions. Derived from `answers` via
 * `quiz.deriveProfile(answers)` and rendered on the result page above the
 * persona-static hero/education content. The profile is the "We analyzed your X"
 * personalized-plan layer; the persona content is the UMP -> UMS bridge.
 *
 * Source of truth for the underlying medicine: `docs/t-suppression-research.md`.
 * Severity scale uses HIGH = highly suppressed (i.e. "level of T suppression"),
 * not high T -- match the meter on the result-page mock.
 */
export const SUPPRESSION_LEVELS = ["low", "normal", "medium", "high"] as const;
export const SuppressionLevelSchema = z.enum(SUPPRESSION_LEVELS);
export type SuppressionLevel = z.infer<typeof SuppressionLevelSchema>;

export const DECLINE_PATTERNS = [
  "stable",
  "early-onset",
  "acute",
  "progressive",
  "stalled-recovery",
] as const;
export const DeclinePatternSchema = z.enum(DECLINE_PATTERNS);
export type DeclinePattern = z.infer<typeof DeclinePatternSchema>;

export const ROOT_CAUSES = [
  "cortisol-dominance",
  "chronic-stress",
  "sleep-deprivation",
  "visceral-adiposity",
  "metabolic-syndrome",
  "lifestyle-load",
  "age-related-decline",
  "overtraining",
  "endocrine-disruption",
  "catabolic-deficit",
  "blocked-testosterone",
  // Forced-only cause for the quiz-trt-switch quiz: the locked meter reads as
  // endogenous production, which exogenous TRT suppresses. Omitted from
  // ROOT_CAUSE_ORDER / scoreRootCauses in boost-profile.ts so it is never
  // scored organically — it surfaces ONLY via forcePrimaryCause.
  "exogenous-suppression",
  // Forced-only cause for the quiz-sleep quiz. The sleep angle's premise is that
  // duration is NOT the problem ("you're in bed the right number of hours"), so
  // `sleep-deprivation` -- whose copy reads "your sleep window is too short" --
  // would contradict the quiz on its own payoff screen. This names the actual
  // mechanism: cortisol failing to bottom out at night, clipping the deep-sleep
  // window where testosterone is made. Omitted from ROOT_CAUSE_ORDER /
  // scoreRootCauses in boost-profile.ts so it is never scored organically -- it
  // surfaces ONLY via forcePrimaryCause.
  "nocturnal-cortisol",
  // Forced-only cause for the quiz-beer quiz. Neither existing estrogen-adjacent
  // cause fits: `visceral-adiposity` blames body fat, which contradicts the
  // "No gut here" answer path, and `blocked-testosterone` leads on the SHBG
  // handcuff rather than the estrogen swap the quiz's ads and both bridges name.
  // This names the swap itself -- the hormone balance tipping toward estrogen --
  // which holds on every path regardless of reported beer volume. Omitted from
  // ROOT_CAUSE_ORDER / scoreRootCauses in boost-profile.ts so it is never scored
  // organically -- it surfaces ONLY via forcePrimaryCause.
  "estrogen-dominance",
] as const;
export const RootCauseSchema = z.enum(ROOT_CAUSES);
export type RootCause = z.infer<typeof RootCauseSchema>;

export const QOL_IMPACT_LEVELS = ["low", "medium", "high"] as const;
export const QolImpactSchema = z.enum(QOL_IMPACT_LEVELS);
export type QolImpact = z.infer<typeof QolImpactSchema>;

/**
 * Evidence chips surface the user's actual quiz answers under the meter so the
 * profile reads as "we used your data" rather than a generic verdict. Each
 * item is a (label, value) pair rendered as a small pill.
 */
export const EvidenceItemSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

/**
 * Personalized "what the user wants from this protocol" pair, derived from the
 * results-want question. Surfaces under the assessment card so every collected
 * answer pays back personalization on the result page.
 */
export const ExpectedOutcomeSchema = z.object({
  label: z.string().min(1),
  copy: z.string().min(1),
});
export type ExpectedOutcome = z.infer<typeof ExpectedOutcomeSchema>;

/**
 * quiz-v7's `concern` answer values -- the five things a user can pick as their
 * main concern. These key quiz-v7's own routing structures (`BRANCH_FLOWS`,
 * `COHORT_PRIORITY`, `CONCERN_TO_RESULTS_WANT`), so a new member here means a new
 * branch flow and a new cohort-gradient question. Do NOT widen it just to theme
 * the result surfaces -- that is what `COHORT_KEYS` below is for.
 */
export const CONCERN_KEYS = ["energy", "muscle", "sex-drive", "focus", "weight-loss"] as const;
export const ConcernKeySchema = z.enum(CONCERN_KEYS);
export type ConcernKey = z.infer<typeof ConcernKeySchema>;

/**
 * Cohorts the Result / Projection / Offer surfaces can theme themselves for, via
 * `COHORT_OVERLAYS` (quizzes/quiz-v7.ts) keyed off `TSuppressionProfile.cohort`.
 *
 * A superset of `CONCERN_KEYS`: every quiz-v7 concern is also a themeable cohort,
 * but a cohort need not be a concern anyone can pick. `"sleep"` (added 2026-07-30
 * for quiz-sleep) and `"beer"` (added 2026-08-14 for quiz-beer) are overlay-only
 * -- no quiz offers either as a `concern` answer, and boost-profile.ts's
 * severity / root-cause / QoL reads of `concern` never see them.
 * This split is why adding a cohort does not force fake entries into quiz-v7's
 * branch-flow and gradient-question maps.
 */
export const COHORT_KEYS = [
  ...CONCERN_KEYS,
  "sleep",
  "beer",
  "cortisol-weight",
  "cortisol-weight-clear",
] as const;
export const CohortKeySchema = z.enum(COHORT_KEYS);
export type CohortKey = z.infer<typeof CohortKeySchema>;

export const TSuppressionProfileSchema = z.object({
  suppressionLevel: SuppressionLevelSchema,
  assessment: z.object({
    label: z.string().min(1),
    headline: z.string().min(1),
    explanation: z.string().min(1),
  }),
  declinePattern: DeclinePatternSchema,
  rootCauses: z.array(RootCauseSchema).min(2).max(3),
  qualityOfLifeImpact: QolImpactSchema,
  evidence: z.array(EvidenceItemSchema).min(2).max(6),
  expectedOutcome: ExpectedOutcomeSchema.optional(),
  cohort: CohortKeySchema.optional(),
});
export type TSuppressionProfile = z.infer<typeof TSuppressionProfileSchema>;

/**
 * WP08 story-sequence result reveal. A `StoryCard` is one full-screen,
 * tap-through beat rendered by `web/src/components/Stories.tsx` between the
 * `building` loader and the (unchanged, rule 13) result page. Cards are
 * produced by `quiz.deriveStories(profile, answers)` -- a pure function of
 * the derived profile (rule 11), same reproducibility contract as
 * `deriveProfile`. See `quizzes/story-cards.ts` for the default 6-card deck
 * and `docs/specs/2026-08-01-competitor-feature-port/wp08-story-results-design.md`
 * for the full design.
 *
 * `visual` selects which data-bound visual `Stories.tsx` renders alongside
 * the card copy (it reads the raw `TSuppressionProfile`, not fields on the
 * card, so the deck stays plain serializable data):
 * - "meter": the result page's semantic suppression gradient meter.
 * - "chip-grid": the profile's `evidence` pills played back.
 * - "pattern": a static decorative motif (no data binding, no animation).
 */
export const STORY_VISUALS = ["meter", "pattern", "chip-grid"] as const;
export const StoryVisualSchema = z.enum(STORY_VISUALS);
export type StoryVisual = z.infer<typeof StoryVisualSchema>;

export const StoryCardSchema = z.object({
  /** Stable analytics key (e.g. "duration") -- carried as `question_id` on the `story_viewed` event. */
  id: z.string().min(1),
  eyebrow: z.string().min(1).optional(),
  head: z.string().min(1),
  body: z.string().min(1).optional(),
  visual: StoryVisualSchema.optional(),
  /** Per-card advance label ("Show me", "Next", "See my full assessment"). */
  cta: z.string().min(1),
});
export type StoryCard = z.infer<typeof StoryCardSchema>;

/**
 * A full story deck. Bounded 3-8: fewer than 3 isn't a "sequence", more than
 * 8 stretches the primer past the point of bridging to the result. Boot/CI
 * validation for a function-valued field happens in the validate-quiz
 * walker (mirrors how `deriveProfile` output is exercised today), not here.
 */
export const StoryDeckSchema = z.array(StoryCardSchema).min(3).max(8);
export type StoryDeck = z.infer<typeof StoryDeckSchema>;

/**
 * Answer-recap pill: plays back one recorded answer as a small "we heard
 * you" dashboard chip on the Offer page (e.g. "Age: 40-49"). `label` is the
 * authored category prefix ("Age"), NOT the answer text -- the answer text
 * is resolved at RENDER time from `answers[questionId]` against the CURRENT
 * quiz definition's option labels (see `web/src/lib/planRecap.ts`). Nothing
 * about the resolved display text is ever persisted; only the raw answer
 * value lives in `quiz_submissions.answers`, same as today.
 */
export type PlanRecapPill = {
  label: string;
  questionId: string;
};

/**
 * One row of the dated transformation timeline. `offsetDays` is resolved to
 * a real calendar date at RENDER time (`now + offsetDays`, formatted
 * `en-US` "Month Day") -- never persisted, so the date is always relative to
 * when the page is actually viewed, not to when the quiz was answered.
 */
export type PlanRecapTimelineRow = {
  offsetDays: number;
  label: string;
};

export type PlanRecapConfig = {
  pills: PlanRecapPill[];
  timeline?: PlanRecapTimelineRow[];
};

/**
 * WP09 mid-quiz diagnosis dashboard. Teardown source: Primal Viking's step
 * 15/27 "Your Testosterone Diagnosis" (animated gauge + metric bars, then
 * MORE questions). Rendered by `web/src/components/SummaryDashboard.tsx` at
 * the `summary` screen type above, produced by `quiz.deriveSummary(answers)`
 * -- a pure function of answers-so-far, same reproducibility contract as
 * `deriveProfile` (rule 11).
 *
 * Severity floor (rule 12a analog, three independently-sufficient layers):
 *   1. Vocabulary: no "average"/"optimal" member exists, so no derivation can
 *      render an absolving badge.
 *   2. Schema: every score is capped at MAX_SUMMARY_SCORE (58) by Zod parse
 *      -- a derivation returning 59+ fails loudly (walker/CI-fatal).
 *   3. Walker lock: tests/quizzes/summary-floor.test.ts walks every reachable
 *      prefix at a fixture quiz's summary-screen position and asserts parse
 *      success + floored vocabulary + score cap + metrics >= 2.
 *
 * See docs/specs/2026-08-01-competitor-feature-port/wp09-midquiz-dashboard-design.md.
 */
export const SUMMARY_SEVERITIES = ["critical", "low", "below-average"] as const;
export const SummarySeveritySchema = z.enum(SUMMARY_SEVERITIES);
export type SummarySeverity = z.infer<typeof SummarySeveritySchema>;

/** Primal Viking's `MAX_METRIC = 58` cap, adopted verbatim: no combination of
 * answers can ever score "healthy" -- the numeric analog of the severity
 * vocabulary floor above. */
export const MAX_SUMMARY_SCORE = 58;

export const SummaryMetricSchema = z.object({
  key: z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]*$/, "metric key must be lowercase, may contain digits/_/-, no leading _"), // rule 22 charset
  label: z.string().min(1),
  score: z.number().int().min(0).max(MAX_SUMMARY_SCORE),
  severity: SummarySeveritySchema,
  detail: z.string().min(1),
});
export type SummaryMetric = z.infer<typeof SummaryMetricSchema>;

/**
 * One row of the sign tally. `state` is three-valued on purpose: a screen
 * placed mid-flow has signs the quiz has not asked about yet, and rendering
 * those as "absent" would tell the reader he does not have something he was
 * never asked.
 */
export const SUMMARY_TALLY_STATES = ["confirmed", "absent", "unasked"] as const;
export const SummaryTallyStateSchema = z.enum(SUMMARY_TALLY_STATES);
export type SummaryTallyState = z.infer<typeof SummaryTallyStateSchema>;

export const SummaryTallyRowSchema = z.object({
  key: z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]*$/, "tally row key must be lowercase, no leading _"), // rule 22 charset
  label: z.string().min(1),
  state: SummaryTallyStateSchema,
});
export type SummaryTallyRow = z.infer<typeof SummaryTallyRowSchema>;

/**
 * The count-the-signs-out-loud presentation. Added 2026-09-04 for
 * cortisol-weightloss, whose ad promises eight signs -- so the mid-quiz
 * screen counts eight signs rather than scoring abstract axes.
 *
 * `confirmed` and `total` are validated against `rows` so the headline number
 * and the list cannot disagree. They are stored rather than derived at render
 * time because the component must not do arithmetic the deriver owns
 * (rule 11: the dashboard is produced by `deriveSummary`, the component is
 * purely presentational).
 */
export const SummaryTallySchema = z
  .object({
    confirmed: z.number().int().min(0),
    total: z.number().int().min(1),
    rows: z.array(SummaryTallyRowSchema).min(2),
  })
  .superRefine((t, ctx) => {
    if (t.rows.length !== t.total) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `tally total (${t.total}) must equal the number of rows (${t.rows.length})`,
        path: ["total"],
      });
    }
    const confirmed = t.rows.filter((r) => r.state === "confirmed").length;
    if (confirmed !== t.confirmed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `tally confirmed (${t.confirmed}) must equal the number of confirmed rows (${confirmed})`,
        path: ["confirmed"],
      });
    }
  });
export type SummaryTally = z.infer<typeof SummaryTallySchema>;

/**
 * A summary screen renders EXACTLY ONE presentation: the original gauge +
 * metric bars, or the sign tally. `gauge` and `metrics` are optional only so
 * the tally can omit them; a gauge presentation still requires both, which is
 * what the refine enforces. No live quiz used this schema before 2026-09-04,
 * so relaxing the two fields breaks no caller.
 */
export const SummaryDashboardSchema = z
  .object({
    gauge: z
      .object({
        score: z.number().int().min(0).max(MAX_SUMMARY_SCORE),
        label: z.string().min(1),
      })
      .optional(),
    metrics: z.array(SummaryMetricSchema).min(2).max(4).optional(),
    tally: SummaryTallySchema.optional(),
    footnote: z.string().min(1).optional(),
  })
  .superRefine((d, ctx) => {
    const hasGauge = d.gauge !== undefined && d.metrics !== undefined;
    const hasTally = d.tally !== undefined;
    if (hasGauge === hasTally) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "summary dashboard must declare exactly one presentation: either gauge + metrics, or tally",
        path: hasTally ? ["tally"] : ["gauge"],
      });
    }
  });
export type SummaryDashboard = z.infer<typeof SummaryDashboardSchema>;

export const SIGNAL_BANDS = ["mild", "moderate", "strong", "very-strong"] as const;
export type SignalBand = (typeof SIGNAL_BANDS)[number];

export type SignalSummaryData = {
  band: SignalBand;
  bandIndex: 0 | 1 | 2 | 3;
  message: string;
  bullets: readonly string[];
};

/**
 * A run of body text, flagged by where it came from.
 *
 * `customerProvided` is the user's own words, rendered green. `accent` is an
 * authored phrase rendered orange, and it is the only field a DERIVED body may
 * set for itself -- static prose declares its accents as `bodyAccents`
 * substrings instead, which `Question.tsx` applies at render time.
 *
 * The split exists because `bodyAccents` is validated against the STATIC
 * `body` at parse time and matched by substring at render time, so a phrase
 * that only ever appears in a derived paragraph either fails to parse or
 * silently no-ops. A count sentence like "You've already named 3 of them"
 * cannot be written as a substring of prose that does not contain the number,
 * which is exactly the case `cortisol-weightloss/info-absolution` needed. A
 * deriver is already hand-segmenting to flag `customerProvided`, so letting it
 * mark its own accent adds no new authoring burden -- and the parse-time
 * safety `bodyAccents` gives up here has to be replaced by a test that the
 * accent occurs in the paragraphs on EVERY bucket (see
 * `tests/quizzes/cortisol-weightloss-summary.test.ts`).
 *
 * `applyAccents` skips runs already marked, so the two mechanisms compose on a
 * screen that uses both.
 */
export type RecapSegment = { text: string; customerProvided: boolean; accent?: boolean };
export type WeightlossMilestone = { label: string; dateLabel: string; title?: string; body: string };

export type WeightlossV2Result = {
  signal: SignalSummaryData;
  recap: readonly RecapSegment[];
  target: null | {
    isoDate: string;
    /** "Sep 18" -- the tight chart marker and milestone rows. */
    shortLabel: string;
    /** "September 18, 2026" -- the plan intro sentence. */
    longLabel: string;
    /** "September 18" -- the plan heading, which carries no year. */
    monthDayLabel: string;
    pinPercent: number;
    alignRight: boolean;
  };
  planHeading: string;
  planIntro: readonly RecapSegment[];
  milestones: readonly WeightlossMilestone[];
};

export type WeightlossV2PostQuizConfig = {
  kind: "weightloss-v2";
  deriveResult: (answers: Answers) => WeightlossV2Result;
  submitOnApplying: true;
  plan: { eyebrow: string; cta: string };
  offer: {
    kicker: string;
    heading: string;
    lede: string;
    productName: string;
    productDescription: string;
    imageSrc: string;
    imageAlt: string;
    /**
     * Both OPTIONAL, and both omitted by quiz-weightloss-v2 as of 2026-08-11.
     *
     * An offer may state a discount without stating a price. That is the live
     * configuration: the quiz advertises 67% off and lets the PDP be the single
     * place a figure is quoted, so a promo change is one edit on the storefront
     * rather than two systems that can disagree about what the customer pays.
     * It also keeps the quiz from asserting a price that, for this product,
     * only exists above the Shopify product layer (see the provenance note in
     * quizzes/quiz-weightloss-v2.ts).
     *
     * `WeightlossOffer` renders the price row only when `price` is present, and
     * the strike-through/savings badge only when BOTH are -- a compare-at with
     * nothing to compare against is a dangling claim.
     */
    price?: string;
    compareAtPrice?: string;
    /**
     * Aggregate customer rating, e.g. `{ value: 4.77, outOf: 5 }`. Authored
     * here rather than hardcoded in the component so growth can refresh it from
     * the live PDP (the Shopify `reviews.rating` metafield on
     * /products/30-day-supply-starter-kit) in one line, with no code change --
     * the same remedy applied to `stats`.
     *
     * Optional on purpose: omit the field and the star row does not render at
     * all. An absent claim is correct; an invented one is not. Never round a
     * value up to a perfect score -- the renderer fills the star track
     * proportionally so a non-integer average is shown honestly.
     */
    rating?: { value: number; outOf: number };
    /**
     * Review-count label rendered beside the rating, e.g. "4,959 reviews".
     * Optional for the same reason as `rating`: source it from the live PDP
     * (`reviews.rating_count`) or omit it rather than inventing a figure. Do
     * not call the reviews "verified" unless the review app actually reports
     * every one of them as a verified buyer.
     */
    reviewCount?: string;
    /**
     * Social-proof chips rendered under the product image. Authored here, not
     * in the component, so growth can chat-edit them against
     * `docs/product-brief.md` (the canonical source for customer counts).
     * Omit the field to render no stats row at all.
     */
    stats?: readonly string[];
    features: readonly string[];
    /**
     * Section label rendered above `features`. The approved copy deck heads
     * that bullet list "Restoration, not replacement" -- a positioning claim,
     * not a spec list -- so the label is authored rather than hardcoded to
     * "Product features" in the component. Omit it and the list renders under
     * the component's generic heading, as it did before.
     */
    featuresLabel?: string;
    /**
     * "How it works" prose. The approved copy deck states the fulfilment terms
     * as one paragraph ("your first 30-day supply ships now at 50% off... a
     * fresh 30-day supply arrives every 30 days at the same $59, and you can
     * skip, pause, or cancel anytime") rather than as a milestone sequence.
     *
     * These are billing claims: they are true only of the SUBSCRIPTION selling
     * plan on /products/30-day-supply-starter-kit ("Delivered every 30 days",
     * 0% further adjustment, so recurring price stays the $59 variant price).
     * The same product has `requiresSellingPlan: false` and the brief lists a
     * separate one-time-purchase URL, so do not reword this into a claim that
     * auto-ship happens regardless of what the customer selects on the PDP.
     */
    howItWorksBody?: string;
    /**
     * Formula section: label + the "what is actually in the bottle" claim that
     * heads the ingredient grid. Optional -- omit it and the grid renders under
     * its own heading with no lead-in, as it did before.
     */
    formula?: { label: string; body: string };
    /**
     * Price-block lines rendered beneath the price row, e.g. "50% off for
     * life". Sourced from `docs/product-brief.md`, which states the price as
     * `$59/month ("50% off for life" -- regular price $118). Less than $2/day.`
     * Omit the field to render the bare price row.
     */
    pricePoints?: readonly string[];
    /**
     * Guarantee panel copy. Authored explicitly rather than regex-recovered
     * from a `features` bullet -- reworded copy used to delete the panel with
     * no type error and no test failure.
     */
    guarantee?: { label: string; body: string };
    /**
     * Surveyed-outcome closer rendered after the guarantee, e.g. "In a survey
     * of 1,000 customers, 91% reported higher energy...". Authored here for the
     * same reason as `stats` and `rating`: the figures come from
     * `docs/product-brief.md` and growth refreshes them in one line.
     *
     * Optional on purpose -- omit the field and no survey line renders at all.
     * Never restate the surveyed population or round a percentage; if a figure
     * is ever unsourceable, delete the field rather than approximating it.
     */
    surveyProof?: string;
    ingredients: readonly { name: string; dose: string; imageSrc: string; imageAlt: string }[];
    cta: string;
    href: string;
  };
};

export type PersonaQuiz = QuizDefinition & {
  kind?: "persona";
  /**
   * When true, the persona flow embeds an email-capture gate INSIDE the
   * `applying` phase loader (Variant C): the "applying your discount" loader
   * ticks, then reveals an email field that must be filled before the PDP
   * redirect fires. Submission is DEFERRED to the gate (MarsMenQuiz skips the
   * building-phase submit) so the captured email + implicit marketing-consent
   * land on the single `quiz_submissions` row that triggers Klaviyo — same
   * defer-to-gate contract as calculator quizzes (`CalculatorQuiz.emailGate`).
   *
   * When false/undefined the persona flow is byte-identical to the ungated
   * path (quiz-v6): submit at building with email=null, plain ApplyingDiscount,
   * no gate. This flag is a runtime-rendering concern only — it does NOT touch
   * `classifier` / `deriveProfile`, so a gated clone stays regression-identical
   * to its source quiz. First instance: `quizzes/quiz-v6-gated.ts`.
   */
  emailGate?: boolean;
  /**
   * How many screens this quiz actually ASKS, when `branching` skips some of
   * the ones it defines.
   *
   * The progress bar's denominator defaults to the screens visible to the
   * respondent's arm (`visibleQuestions().length`), which is exact for a quiz
   * whose branching walks everything it defines -- 28 of the 29 live
   * quiz-arms. `quiz-v7` is the exception: it defines 22 screens and asks
   * exactly 9 on every reachable path, because its five concern branches are
   * mutually exclusive and equal-length. Left to the default its bar stops at
   * 41% and screen readers hear "of 22".
   *
   * DECLARED rather than derived, because neither derivation is affordable or
   * sound. Projecting the remaining path from the answers so far gives
   * quiz-v7 the sequence 2,2,9,9,9,9,9,9,9 -- `branching` returns null while
   * `concern` is unanswered -- so the bar reads 50% on screen 2 and then goes
   * BACKWARDS to 22% on screen 3. And a node-memoised "longest reachable
   * path" is unsound because branching reads answers from EARLIER screens;
   * carrying them makes it a full path enumeration, which on quiz-v6 is
   * 614,400 walks per render.
   *
   * Set it ONLY when every reachable path is the same length, and only to
   * that length. `tests/quizzes/progress-total.test.ts` walks every live
   * quiz's real branching in every arm and fails both ways: a declared number
   * that any path contradicts, and an UNdeclared quiz whose paths are shorter
   * than its screen count (i.e. a new branching quiz that forgot to declare).
   * That second half is the one that matters -- it is what stops the next
   * branching quiz from silently shipping a bar that cannot fill.
   */
  progressTotal?: number;
  locale?: Locale;
  /**
   * When set, the `offer` reducer phase renders `ScratchOffer` INSTEAD of
   * the plain `Offer` component -- a canvas scratch-off card that must be
   * revealed (by scratching, or the tap-to-reveal fallback) before the CTA
   * unlocks. This is a runtime-render flag only, exactly like `emailGate`
   * above: it does NOT touch `classifier` / `deriveProfile`, and the
   * reducer-level state machine still has exactly one `offer` phase. When
   * unset, the offer phase is byte-identical to today (`Offer.tsx`,
   * unmodified). See `web/src/components/OfferPhase.tsx` for the swap and
   * `web/src/components/ScratchOffer.tsx` / `ScratchCard.tsx` for the
   * mechanic. First instance: test fixtures only (WP06) -- no live quiz
   * opts in yet.
   */
  offerScratch?: {
    /** The revealed headline value, e.g. "50% OFF". */
    prize: string;
    /** Optional supporting line under the prize. */
    prizeSub?: string | undefined;
    /** CTA label; defaults to the same copy as the plain Offer CTA. */
    cta?: string | undefined;
  };
  /**
   * When set, the `offer` reducer phase's plain `Offer` surface (NOT
   * `ScratchOffer` -- see `offerScratch` above) renders an additive recap
   * section BELOW the existing CTA, as scroll depth: the user's own
   * recorded answers played back as small chips ("Age: 40-49 ... Goal:
   * Athletic"), plus an optional dated transformation timeline ("by August
   * 31"). This is a runtime-render flag only, exactly like `emailGate` /
   * `offerScratch`: it does NOT touch `classifier` / `deriveProfile`, and
   * the reducer-level state machine is unchanged (still exactly one
   * `offer` phase). When `offerScratch` is ALSO set, `OfferPhase` swaps to
   * `ScratchOffer` and this recap section does not render at all -- recap
   * is additive to the plain `Offer` surface only (see `OfferPhase.tsx`).
   * When unset, or when every referenced pill fails to resolve (stale/
   * removed question, unanswered question -- see `web/src/lib/planRecap.ts`),
   * the Offer page is byte-identical to today. First instance: test
   * fixtures only (WP07) -- no live quiz opts in yet.
   */
  planRecap?: PlanRecapConfig;
  /**
   * WP08 story-sequence result reveal. When set, the reducer's `completed`
   * dispatch (from `building`) is given `stories: true`, which routes to a
   * new `stories` phase -- a paced, tap-through primer sequence -- BEFORE
   * the unchanged (rule 13) `result` phase, instead of going there directly.
   * Pure function of (profile, answers), same reproducibility contract as
   * `deriveProfile` (rule 11): no persona-authored copy, no persona key
   * input. `quizzes/story-cards.ts` ships a default 6-card deck
   * (`buildDefaultStories`) quizzes can adopt with one line:
   * `deriveStories: (p, a) => buildDefaultStories(p, a)`. This does NOT
   * touch `classifier` / `deriveProfile`, and the state machine still has
   * exactly one path through `stories` (there is no branching inside it).
   * When unset, or when it throws at render, MarsMenQuiz never enters
   * `stories` / falls straight through to `result` -- the diagnostic must
   * never be blocked by the primer. First instance: test fixtures only
   * (WP08) -- no live quiz opts in yet. See `web/src/components/Stories.tsx`.
   */
  deriveStories?: (profile: TSuppressionProfile, answers: Answers) => StoryCard[];
  /**
   * WP09 mid-quiz diagnosis dashboard. Pure function of answers-so-far
   * (rule 11), producing the content for a `summary`-type screen (see
   * `SummaryQuestionSchema` above) -- the screen carries only flow data,
   * this seam carries the diagnostic content. `defineQuiz` throws at parse
   * time if any question is `type: "summary"` and this is not defined (loud
   * boot failure, rule 3); the inverse (defining this without any `summary`
   * screen) is allowed and inert. Reachable-prefix validation happens in the
   * `scripts/validate-quiz.ts` walker, not here (mirrors `deriveStories`).
   * First instance: test fixtures only (WP09) -- no live quiz opts in yet.
   * See `web/src/components/SummaryDashboard.tsx`.
   */
  deriveSummary?: (answers: Answers) => SummaryDashboard;
  postQuiz?: WeightlossV2PostQuizConfig;
  /**
   * Quiz-supplied template tokens, resolved at render time into any
   * interpolated string (see `web/src/lib/interpolate.ts`). Pure function of
   * answers-so-far (rule 11), same contract as `deriveProfile`.
   *
   * This is the generic seam behind answer-reactive editorial beats: the
   * `cortisol-story` quiz returns `{ signCount: "5" }` and its editorial
   * bodies write `{signCount}`, so no cortisol-specific knowledge leaks into
   * shared render code. `{name}` (WP03) is NOT sourced here -- it keeps its
   * own special case because it carries fallback + punctuation-cleanup rules
   * that a plain string substitution must not inherit.
   *
   * Keys must be `[a-zA-Z][a-zA-Z0-9_]*` to match the token grammar. A quiz
   * without this field interpolates exactly as before (live-quiz guarantee).
   */
  deriveTokens?: (answers: Answers) => Record<string, string>;
  branching: (answers: Answers, currentQuestionId: string) => string | null;
  classifier: (answers: Answers) => PersonaKey;
  deriveProfile?: (answers: Answers) => TSuppressionProfile;
};

export const CALCULATOR_LAYOUTS = ["stepped", "single-page"] as const;
export type CalculatorLayout = (typeof CALCULATOR_LAYOUTS)[number];

export type CalculatorQuiz = QuizDefinition & {
  kind: "calculator";
  /**
   * Controls how `CalculatorQuizRunner` lays out the question phase:
   * - `"stepped"` (default): one number question per screen with a Continue
   *   button per question. Matches the first calculator (free-t).
   * - `"single-page"`: intro hero + all number questions stacked on one screen
   *   with a single Submit button. For short calculators (1-4 inputs) where
   *   the user already has the values in hand and stepping is friction.
   *   `branching` is still required and validated at boot, but is not invoked
   *   during single-page rendering -- the runner walks `quiz.questions` in
   *   array order. If a future single-page calculator needs conditional
   *   inputs, extend this layout's renderer first.
   */
  layout?: CalculatorLayout;
  /**
   * When true, the runner inserts a commitment loader and an email-capture
   * gate between compute and the result page. Submission is deferred until
   * the gate is filled (or explicitly skipped) so the captured email + the
   * implicit marketing-consent flag land on the `quiz_submissions` row that
   * triggers Klaviyo. When false/undefined, compute -> result is immediate
   * (the legacy free-t path; preserves back-compat with the first calculator
   * before the lead-capture surface was designed).
   */
  emailGate?: boolean;
  branching: (answers: Answers, currentQuestionId: string) => string | null;
  compute: (answers: Answers) => CalculatorResult;
};

/**
 * A quiz that runs the persona FLOW but produces a diagnosis instead of a
 * persona key.
 *
 * WHY THIS IS A THIRD KIND rather than a `PersonaQuiz` with an optional
 * classifier. `classifier` must return one of the six testosterone archetypes
 * (rule 1), and `quiz_persona` is stamped on the PDP hand-off as the record of
 * which archetype was served (rule 29). A cognition quiz has no honest value
 * to put there, and a fixed one -- `performance`, say -- would corrupt that
 * measure for every quiz that fills it properly. Rule 26 already settles the
 * symmetric case by naming a new kind as the answer ("do not bolt persona
 * fields onto `CalculatorQuiz`"), so this follows that precedent instead of
 * widening `PersonaQuiz` with a mode it should not have.
 *
 * It shares the persona flow's phases, progress bar, analytics and submission
 * path. It differs in exactly one place: `deriveFocusProfile` stands in for
 * `classifier` + `deriveProfile`.
 *
 * Backend counterpart: `DIAGNOSTIC_SLUGS` in `api/schemas/submission.py`,
 * which accepts a null persona for these slugs the same way `CALCULATOR_SLUGS`
 * already does -- a separate tuple, so the two reasons a row has no persona
 * stay distinguishable in the data.
 */
export type DiagnosticQuiz = QuizDefinition & {
  kind: "diagnostic";
  /**
   * Pure function of `answers`, reproducible server-side from the persisted
   * submission -- same contract as `deriveProfile` (rule 11) and `compute`.
   */
  deriveFocusProfile: (answers: Answers) => FocusProfile;
  /**
   * The verdict PROSE pack this quiz renders: cause labels, cause card
   * bodies, and (through the deriver's own `copy` seam) the assessment
   * headline and explanation. Defaults to English at the call site.
   *
   * It lives on the quiz rather than being picked by the widget because the
   * page reads those strings by TWO routes -- the deriver bakes the
   * assessment prose into the profile, and the breakdown grid looks the cause
   * label up again to render its own cell. With the widget hardcoding one
   * pack, a locale wrapper passing another through the deriver's seam (the
   * documented way, as `quiz-v6-es-profile.ts` does it) would translate the
   * assessment card and leave the grid's cause names in English, with nothing
   * to catch it: the types are satisfied and no test can see it. One field,
   * read once, threaded to both.
   */
  copy?: FocusCopyPack;
  progressTotal?: number;
  locale?: Locale;
  theme?: QuizTheme;
  branching: (answers: Answers, currentQuestionId: string) => string | null;
};

export type QuestionQuiz = PersonaQuiz | CalculatorQuiz | DiagnosticQuiz;
export type Quiz = QuestionQuiz | StoryFunnel;

export function isStoryFunnel(quiz: Quiz): quiz is StoryFunnel {
  return quiz.kind === "story-funnel";
}

export function isQuestionQuiz(quiz: Quiz): quiz is QuestionQuiz {
  return isPersonaQuiz(quiz) || isCalculatorQuiz(quiz) || isDiagnosticQuiz(quiz);
}

export function isCalculatorQuiz(quiz: Quiz): quiz is CalculatorQuiz {
  return quiz.kind === "calculator";
}

export function isDiagnosticQuiz(quiz: Quiz): quiz is DiagnosticQuiz {
  return quiz.kind === "diagnostic";
}

/**
 * Narrow to a persona quiz POSITIVELY.
 *
 * Before the diagnostic kind existed, callers reached the persona type by
 * elimination -- `!isCalculatorQuiz(quiz)` -- which was sound while the union
 * had two members and silently wrong the moment it had three. Adding the third
 * kind surfaced 25 type errors across `MarsMenQuiz.tsx` and
 * `scripts/validate-quiz.ts`, every one of them a site reading
 * `deriveProfile` / `classifier` / `postQuiz` off a value that might now be a
 * diagnostic quiz.
 *
 * Prefer this guard over negating another one, so the next kind added widens
 * the union without silently changing what any existing branch believes it
 * holds. `kind` is optional on `PersonaQuiz`, so an undefined kind counts as
 * persona -- `defineQuiz` stamps it, but the type permits its absence.
 */
export function isPersonaQuiz(quiz: Quiz): quiz is PersonaQuiz {
  return quiz.kind === undefined || quiz.kind === "persona";
}

/**
 * True when the quiz asks the user for an email.
 *
 * `emailGate` is a runtime-render flag on `PersonaQuiz` and `CalculatorQuiz`
 * and does NOT exist on `DiagnosticQuiz`, so the `in` narrow is required --
 * reading `quiz.emailGate` directly does not compile against the union. It
 * returns a plain boolean rather than a type predicate for the same reason:
 * there is no single narrowed type that both gated kinds share.
 *
 * The admin uses this to decide whether to render the Emails Collected card
 * and the two gate rows. Deriving that from the DATA instead (a non-zero
 * count) would hide the rows on exactly the gate that is converting at zero,
 * which is the case most worth seeing.
 */
export function isGatedQuiz(quiz: Quiz): boolean {
  return "emailGate" in quiz && quiz.emailGate === true;
}

/**
 * Defines a diagnostic-kind quiz. Mirrors `defineCalculatorQuiz`: the whole
 * definition is validated by the shared schema, and the kind is stamped on the
 * returned object so a caller cannot forget it.
 */
export function defineDiagnosticQuiz(quiz: DiagnosticQuiz): DiagnosticQuiz {
  QuizDefinitionSchema.parse({
    id: quiz.id,
    slug: quiz.slug,
    kind: "diagnostic",
    meta: quiz.meta,
    questions: quiz.questions,
    experiment: quiz.experiment,
    capture: quiz.capture,
  });
  if (typeof quiz.deriveFocusProfile !== "function") {
    throw new Error(
      `Diagnostic quiz "${quiz.slug}" must supply deriveFocusProfile.`,
    );
  }
  // Same check `defineQuiz` runs (rule 31): `progressTotal` is the progress
  // bar's denominator, so a value that cannot be a screen count must fail at
  // boot rather than render a bar that can never fill or overfills.
  if (quiz.progressTotal !== undefined) {
    const n = quiz.progressTotal;
    if (!Number.isInteger(n) || n < 1 || n > quiz.questions.length) {
      throw new Error(
        `Quiz "${quiz.slug}" declares progressTotal=${n}, which must be a whole number ` +
          `between 1 and the number of screens (${quiz.questions.length}).`,
      );
    }
  }
  return { ...quiz, kind: "diagnostic" };
}

/**
 * Defines a persona-routing quiz with full type-checking. Use this in
 * `quizzes/<slug>.ts`. The first quiz, Boost, was built with this helper.
 *
 * Example:
 *   export const boostQuiz = defineQuiz({
 *     id: "quiz-v6", slug: "quiz-v6",
 *     meta: { ... },
 *     questions: [ ... ],
 *     capture: { ... },
 *     branching: (answers, current) => "next-id",
 *     classifier: (answers) => "trt-alternative",
 *   });
 */
export function defineQuiz(quiz: PersonaQuiz): PersonaQuiz {
  QuizDefinitionSchema.parse({
    id: quiz.id,
    slug: quiz.slug,
    kind: quiz.kind ?? "persona",
    meta: quiz.meta,
    questions: quiz.questions,
    experiment: quiz.experiment,
    capture: quiz.capture,
  });
  // WP09: a "summary" screen without a paired deriveSummary would render an
  // empty/undefined dashboard mid-quiz -- fail loudly at boot (rule 3)
  // instead of silently at runtime. The inverse (deriveSummary defined with
  // no "summary" screen anywhere) is allowed and inert.
  const hasSummaryScreen = quiz.questions.some((q) => q.type === "summary");
  if (hasSummaryScreen && !quiz.deriveSummary) {
    throw new Error(
      `Quiz "${quiz.slug}" declares a "summary" screen but does not define deriveSummary. ` +
        `Every "summary" screen must be paired with quiz.deriveSummary (see quizzes/_schema.ts).`,
    );
  }
  // `progressTotal` is the progress bar's denominator, so a nonsense value is
  // a visibly broken bar (a fraction over 1, or a divide-by-zero) rather than
  // a subtle miscount. Fail at boot (rule 3). The value being CORRECT -- equal
  // to every reachable path length -- is not checkable here without walking
  // the quiz; tests/quizzes/progress-total.test.ts does that.
  if (quiz.progressTotal !== undefined) {
    const n = quiz.progressTotal;
    if (!Number.isInteger(n) || n < 1 || n > quiz.questions.length) {
      throw new Error(
        `Quiz "${quiz.slug}" declares progressTotal=${n}, which must be a whole number ` +
          `between 1 and its screen count (${quiz.questions.length}). ` +
          `progressTotal is the number of screens the quiz ASKS, and branching can only ` +
          `skip screens, never invent them.`,
      );
    }
  }
  const hasSignalSummaryScreen = quiz.questions.some((q) => q.type === "signal-summary");
  if (hasSignalSummaryScreen && quiz.postQuiz?.kind !== "weightloss-v2") {
    throw new Error(
      `Quiz "${quiz.slug}" declares a "signal-summary" screen but does not define the weightloss-v2 postQuiz contract.`,
    );
  }
  return { ...quiz, kind: "persona" };
}

/**
 * Defines a calculator-style quiz. The `compute` function is a pure function
 * of `answers` that produces a `CalculatorResult` — the calculator-mode analog
 * of `deriveProfile`. Same input -> same output, so the result is reproducible
 * server-side from the persisted submission.
 *
 * Calculator quizzes do not have a persona classifier or `deriveProfile`. They
 * render the calculator-result surface instead of the persona narrative.
 *
 * Example:
 *   export const freeTQuiz = defineCalculatorQuiz({
 *     id: "free-t", slug: "free-t",
 *     meta: { ... },
 *     questions: [ ... ],
 *     branching: (answers, current) => "next-id",
 *     compute: (answers) => ({ hero: ..., status: ..., ... }),
 *   });
 */
export function defineCalculatorQuiz(quiz: CalculatorQuiz): CalculatorQuiz {
  QuizDefinitionSchema.parse({
    id: quiz.id,
    slug: quiz.slug,
    kind: "calculator",
    meta: quiz.meta,
    questions: quiz.questions,
    experiment: quiz.experiment,
    capture: quiz.capture,
  });
  return { ...quiz, kind: "calculator" };
}
